// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const link = getLink(state, ownProps.fields.imageLink);
    return { pageUrl: link?.url };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ImageStyles {
    wrapper?: InjectableCss;
    image?: LazyImageProps;
    imageLink?: LinkPresentationProps;
}

export const imageStyles: ImageStyles = {
    image: {
        css: css`
            max-width: 100%;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    imageLink: {
        css: css`
            width: 100%;
            > span {
                width: 100%;
            }
        `,
    },
};

function getFocalPointStyles(
    focalPoint:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight",
) {
    let focalPointStyles;
    switch (focalPoint) {
        case "topLeft":
            focalPointStyles = "background-position: left top;";
            break;
        case "topCenter":
            focalPointStyles = "background-position: center top;";
            break;
        case "topRight":
            focalPointStyles = "background-position: right top;";
            break;
        case "centerLeft":
            focalPointStyles = "background-position: left center;";
            break;
        case "center":
            focalPointStyles = "background-position: center center;";
            break;
        case "centerRight":
            focalPointStyles = "background-position: right center;";
            break;
        case "bottomLeft":
            focalPointStyles = "background-position: left bottom;";
            break;
        case "bottomCenter":
            focalPointStyles = "background-position: center bottom;";
            break;
        case "bottomRight":
            focalPointStyles = "background-position: right bottom;";
            break;
    }

    return focalPointStyles;
}

const styles = imageStyles;

const ClickableBannerImage: React.FunctionComponent<Props> = ({ fields, pageUrl }: Props) => {
    const image = <LazyImage src={fields.imageUrl} altText={fields.altText} {...styles.image} />;

    return (
        <StyledWrapper {...styles.wrapper}>
            {pageUrl ? (
                <Link href={pageUrl} {...styles.imageLink}>
                    {image}
                </Link>
            ) : (
                image
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ClickableBannerImage),
    definition: {
        group: "Basic",
        icon: "Image",
        fieldDefinitions: [
            {
                name: fields.imageUrl,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.altText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.imageLink,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
        ],
    },
};

export default widgetModule;
